import React from 'react';
import * as Yup from 'yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Form, useFormikContext } from 'formik';
import { MuiForm } from 'lib/Form';
import { GeosuggestFormField } from 'lib/form/fields/GeosuggestFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';
import {
    geoSuggestAddressFormatter,
    isValidAddress
} from 'components/utils/geoSuggest/geoSuggestAddressFormatter';
import { isValidCoordinate } from 'components/utils/isValidCoordinate';

export interface LocationAddressFormData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    geo: any;
    address: string;
    country: string;
    town: string;
    postcode: string;
    summary: string;
    fullAddress: string;
    googleMapsShortUrl: string;
}

export const addressDefaults: LocationAddressFormData = {
    geo: null,
    address: '',
    country: '',
    town: '',
    postcode: '',
    summary: '',
    fullAddress: '',
    googleMapsShortUrl: ''
};

const SUMMARY_DESCRIPTION =
    "If your address doesn't fit the usual form, you can use this to add to it. An example of this is a building name.";

const validationSchema = Yup.object({
    geo: Yup.mixed().required('Please search for and select a location')
});

const GeoAutoFillEffect: React.FC = () => {
    const { values, setValues } = useFormikContext<LocationAddressFormData>();

    React.useEffect(() => {
        if (!values.geo) {
            return;
        }
        const address = geoSuggestAddressFormatter(values.geo);
        if (!isValidAddress(address)) {
            return;
        }

        let googleMapsShortUrl: string | undefined;
        if (values.geo.location && isValidCoordinate([values.geo.location.lat, values.geo.location.lng])) {
            const { lat, lng } = values.geo.location;
            const placeId = values.geo.placeId;
            googleMapsShortUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&query_place_id=${placeId}`;
        }
        setValues(prev => ({ ...prev, ...address, googleMapsShortUrl }));
    }, [values.geo]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

interface LocationAddressFormProps {
    open: boolean;
    onSubmit: (data: LocationAddressFormData) => void;
    onPrevious: () => void;
    initialValues: LocationAddressFormData;
}

export const LocationAddressForm: React.FC<LocationAddressFormProps> = ({
    open,
    onSubmit,
    onPrevious,
    initialValues
}) => {
    if (!open) {
        return null;
    }
    return (
        <MuiForm onSubmit={onSubmit} validationSchema={validationSchema} initialValues={initialValues}>
            {({ submitForm }) => (
                <Form>
                    <Box width="100%" sx={{ px: 3, pt: 1 }}>
                        <Typography variant="body2">
                            Let us know where your location is so it can be displayed properly in your app.
                        </Typography>
                    </Box>
                    <GeoAutoFillEffect />
                    <Box padding={3}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <GeosuggestFormField
                                    name="geo"
                                    label={<MuiFormLabel required>Search for your location</MuiFormLabel>}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="address" label="1st Line of Address" />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="country" label="Country" />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="town" label="Town" />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField name="postcode" label="Postal Code" />
                            </Grid>
                            <Grid size={12}>
                                <TextFormField
                                    name="summary"
                                    label="Extra Information"
                                    description={SUMMARY_DESCRIPTION}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Row align="flex-end" gutter>
                                    <Button onClick={onPrevious} color="primary" variant="outlined">
                                        Back
                                    </Button>
                                    <Button onClick={submitForm} color="primary" variant="contained">
                                        Next
                                    </Button>
                                </Row>
                            </Grid>
                        </Grid>
                    </Box>
                </Form>
            )}
        </MuiForm>
    );
};
